import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import BossSidebar from "../components/BossSidebar";
import ChannelGrid from "../components/ChannelGrid";
import TrackerTabs from "../components/TrackerTabs";
import GuildManager from "../components/GuildManager";
import AuthPrompt from "../components/AuthPrompt";
import UsernameSetupModal from "../components/UsernameSetupModal";
import FightNotificationContainer from "../components/FightNotification";
import FightNotificationLog from "../components/FightNotificationLog";
import { ExpeditionQuickBar } from "../components/ExpeditionQuickBar";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Menu, RefreshCw, Bell, BellOff, X, UserCog } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const POLL_INTERVAL = 5000;

// Image mapping for bosses
const BOSS_IMAGE_MAPPING = {
  "Mano": "https://legends.ml/static/images/lib/monster/2220000.png",
  "Stumpy": "https://legends.ml/static/images/lib/monster/3220000.png",
  "Deo": "https://legends.ml/static/images/lib/monster/3220001.png",
  "King Clang": "https://legends.ml/static/images/lib/monster/5220000.png",
  "Seruf": "https://legends.ml/static/images/lib/monster/4220001.png",
  "Faust": "https://legends.ml/static/images/lib/monster/5220002.png",
  "Giant Centipede": "https://legends.ml/static/images/lib/monster/9500177.png",
  "Timer": "https://legends.ml/static/images/lib/monster/5220003.png",
  "Mushmom": "https://legends.ml/static/images/lib/monster/6130101.png",
  "Dyle": "https://legends.ml/static/images/lib/monster/6220000.png",
  "Zombie Mushmom": "https://legends.ml/static/images/lib/monster/6300005.png",
  "Zeno": "https://legends.ml/static/images/lib/monster/6220001.png",
  "Nine-Tailed Fox": "https://legends.ml/static/images/lib/monster/7220001.png",
  "Tae Roon": "https://legends.ml/static/images/lib/monster/7220000.png",
  "King Sage Cat": "https://legends.ml/static/images/lib/monster/7220002.png",
  "Jr. Balrog": "https://legends.ml/static/images/lib/monster/8130100.png",
  "Eliza": "https://legends.ml/static/images/lib/monster/8220000.png",
  "Snack Bar": "https://legends.ml/static/images/lib/monster/9410015.png",
  "Chimera": "https://legends.ml/static/images/lib/monster/8220002.png",
  "Blue Mushmom": "https://legends.ml/static/images/lib/monster/9400205.png",
  "Snowman": "https://legends.ml/static/images/lib/monster/8220001.png",
  "Headless Horseman": "https://legends.ml/static/images/lib/monster/9400549.png",
  "Manon": "https://legends.ml/static/images/lib/monster/9500174.png",
  "Griffey": "https://legends.ml/static/images/lib/monster/8180001.png",
  "Pianus (16h)": "https://legends.ml/static/images/lib/monster/8510000.png",
  "Pianus (24h)": "https://legends.ml/static/images/lib/monster/8520000.png",
  "BigFoot": "https://legends.ml/static/images/lib/monster/9400575.png",
  "Black Crow": "https://legends.ml/static/images/lib/monster/9400014.png",
  "Leviathan": "https://legends.ml/static/images/lib/monster/8220003.png",
  "Kacchuu Musha": "https://legends.ml/static/images/lib/monster/9400405.png",
  "Dodo": "https://legends.ml/static/images/lib/monster/8220004.png",
  "Anego / Female Boss": "https://legends.ml/static/images/lib/monster/9400121.png",
  "Lilynouch": "https://legends.ml/static/images/lib/monster/8220005.png",
  "Lyka": "https://legends.ml/static/images/lib/monster/8220006.png",
  "Balrog": "https://royals-library.netlify.app/images/monsters/8830000.png"
};

const Dashboard = () => {
  const { user, guilds } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bosses, setBosses] = useState([]);
  const [selectedBossIndex, setSelectedBossIndex] = useState(() => {
    const saved = localStorage.getItem("selectedBossIndex");
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(() => {
    const saved = localStorage.getItem("selectedLocationIndex");
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [savingChannels, setSavingChannels] = useState({});
  const [historyChannels, setHistoryChannels] = useState({});
  // Backend-driven channel history for admin revert (keyed by channel_index)
  const [channelHistoryData, setChannelHistoryData] = useState({});
  const [userName, setUserName] = useState(() => localStorage.getItem("bossTrackerName") || "");
  const [syncToGlobal, setSyncToGlobal] = useState(() => localStorage.getItem("syncToGlobal") !== "false");
  const [syncToGuild, setSyncToGuild] = useState(() => localStorage.getItem("syncToGuild") === "true");
  const [selectedSyncGuildId, setSelectedSyncGuildId] = useState(() => localStorage.getItem("selectedSyncGuildId") || null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    return localStorage.getItem("notificationsEnabled") === "true";
  });
  const [notifiedChannels, setNotifiedChannels] = useState(new Set());
  const hasInitializedNotifications = useRef(false);
  
  // Tracker type: "global", "personal", "guild"
  const [trackerType, setTrackerType] = useState(() => localStorage.getItem("trackerType") || "global");
  const [compactMode, setCompactMode] = useState(() => localStorage.getItem("compactMode") === "true");

  const handleToggleCompact = () => {
    setCompactMode((v) => {
      const next = !v;
      try {
        localStorage.setItem("compactMode", String(next));
      } catch (e) {}
      return next;
    });
  };
  const [selectedGuildId, setSelectedGuildId] = useState(() => localStorage.getItem("selectedGuildId") || null);
  const [showGuildManager, setShowGuildManager] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  
  // View as User mode (Admin only)
  const [viewAsUser, setViewAsUser] = useState(null);
  const [viewAsTrackers, setViewAsTrackers] = useState([]);
  
  // Favorites
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem("bossFavorites");
    return saved ? JSON.parse(saved) : [];
  });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(() => {
    return localStorage.getItem("showFavoritesOnly") === "true";
  });
  
  // Fight notifications
  const [fightNotifications, setFightNotifications] = useState([]);
  const [fightNotificationHistory, setFightNotificationHistory] = useState(() => {
    const saved = localStorage.getItem("fightNotificationHistory");
    return saved ? JSON.parse(saved) : [];
  });
  const [showFightLog, setShowFightLog] = useState(false);
  const [fightNotificationsEnabled, setFightNotificationsEnabled] = useState(() => {
    return localStorage.getItem("fightNotificationsEnabled") !== "false";
  });
  
  // Hunter's Chance notifications (bounty/expedition updates)
  const [hcNotificationsEnabled, setHcNotificationsEnabled] = useState(() => {
    return localStorage.getItem("hcNotificationsEnabled") !== "false";
  });
  
  const seenNotificationIds = useRef(new Set());
  const lastFetchTime = useRef(Date.now());
  
  const pollTimeoutRef = useRef(null);
  const previousBossesRef = useRef([]);

  // Check for View as User mode from URL params
  useEffect(() => {
    const viewAsId = searchParams.get("viewAs");
    if (viewAsId && user?.is_admin) {
      const viewAsData = sessionStorage.getItem("viewAsUser");
      if (viewAsData) {
        try {
          const data = JSON.parse(viewAsData);
          setViewAsUser(data.user);
          setViewAsTrackers(data.personalTrackers || []);
          setTrackerType("personal"); // Automatically switch to personal view
          localStorage.setItem("trackerType", "personal");
          toast.info(`Viewing as ${data.user.username}'s personal tracker`, {
            duration: 5000,
            description: "Click 'Exit View' to return to normal mode"
          });
        } catch (e) {
          console.error("Failed to parse viewAs data:", e);
        }
      }
    }
  }, [searchParams, user]);

  // Exit View as User mode
  const exitViewAsMode = () => {
    setViewAsUser(null);
    setViewAsTrackers([]);
    sessionStorage.removeItem("viewAsUser");
    setSearchParams({});
    setTrackerType("global");
    localStorage.setItem("trackerType", "global");
    toast.success("Exited view-as mode");
  };

  // Favorites handlers
  const handleToggleFavorite = async (bossName) => {
    let newFavorites;
    if (favorites.includes(bossName)) {
      newFavorites = favorites.filter(name => name !== bossName);
    } else {
      newFavorites = [...favorites, bossName];
    }
    
    setFavorites(newFavorites);
    localStorage.setItem("bossFavorites", JSON.stringify(newFavorites));
    
    // Sync to server if logged in
    if (user) {
      try {
        await axios.post(`${API_URL}/api/favorites/toggle`, { boss_name: bossName });
      } catch (error) {
        console.error("Failed to sync favorite to server:", error);
      }
    }
  };

  const handleShowFavoritesOnlyChange = (value) => {
    setShowFavoritesOnly(value);
    localStorage.setItem("showFavoritesOnly", value.toString());
  };

  // Load favorites from server when user logs in
  useEffect(() => {
    const loadFavorites = async () => {
      if (user) {
        try {
          const response = await axios.get(`${API_URL}/api/favorites`);
          if (response.data.favorites && response.data.favorites.length > 0) {
            setFavorites(response.data.favorites);
            localStorage.setItem("bossFavorites", JSON.stringify(response.data.favorites));
          }
        } catch (error) {
          console.error("Failed to load favorites:", error);
        }
      }
    };
    loadFavorites();
  }, [user]);

  // Save settings to localStorage
  const handleUserNameChange = (e) => {
    const name = e.target.value;
    setUserName(name);
    localStorage.setItem("bossTrackerName", name);
  };

  const handleSyncToGlobalChange = (checked) => {
    setSyncToGlobal(checked);
    localStorage.setItem("syncToGlobal", checked.toString());
  };

  const handleSyncToGuildChange = (checked) => {
    setSyncToGuild(checked);
    localStorage.setItem("syncToGuild", checked.toString());
    // If enabling and no guild selected, select "ALL" by default
    if (checked && !selectedSyncGuildId && guilds.length > 0) {
      setSelectedSyncGuildId("ALL");
      localStorage.setItem("selectedSyncGuildId", "ALL");
    }
  };

  const handleSelectedSyncGuildChange = (guildId) => {
    setSelectedSyncGuildId(guildId);
    localStorage.setItem("selectedSyncGuildId", guildId);
  };

  const toggleNotifications = () => {
    setNotificationsEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem("notificationsEnabled", newValue.toString());
      if (newValue) {
        toast.success("Notifications enabled!");
      } else {
        toast.info("Notifications disabled");
      }
      return newValue;
    });
  };

  // Check for status changes and send notifications
  const checkForNotifications = useCallback((newBosses, currentNow) => {
    if (!notificationsEnabled && !fightNotificationsEnabled) {
      hasInitializedNotifications.current = false;
      return;
    }

    const newSystemNotifications = [];
    const isFirstRun = !hasInitializedNotifications.current;

    newBosses.forEach((boss) => {
      boss.locations.forEach((loc) => {
        loc.channels.forEach((channel, idx) => {
          if (!channel || channel.time === 0) return;

          const baseMs = boss.baseTime * 60 * 1000;
          const minSpawnMs = channel.time + baseMs * 0.8;
          const maxSpawnMs = channel.time + baseMs * 1.2;
          const key = `${loc.backendName}-${idx}`;

          // Check if entering window
          if (currentNow >= minSpawnMs && currentNow < maxSpawnMs && !notifiedChannels.has(`${key}-window`)) {
            if (notificationsEnabled && !isFirstRun) {
              newSystemNotifications.push({
                id: `window-${key}-${currentNow}`,
                type: "enter_window",
                boss_name: boss.name,
                bossImage: getBossImage(boss.imageKey),
                channel: idx + 1,
                location_name: loc.locationName,
                timestamp: new Date().toISOString()
              });
            }
            setNotifiedChannels((prev) => new Set([...prev, `${key}-window`]));
          }

          // Check if now UP
          if (currentNow >= maxSpawnMs && !notifiedChannels.has(`${key}-up`)) {
            if (notificationsEnabled && !isFirstRun) {
              newSystemNotifications.push({
                id: `spawned-${key}-${currentNow}`,
                type: "spawned",
                boss_name: boss.name,
                bossImage: getBossImage(boss.imageKey),
                channel: idx + 1,
                location_name: loc.locationName,
                timestamp: new Date().toISOString()
              });
            }
            setNotifiedChannels((prev) => new Set([...prev, `${key}-up`]));
          }
        });
      });
    });

    if (newSystemNotifications.length > 0 && !isFirstRun) {
      setFightNotifications(prev => [...newSystemNotifications, ...prev].slice(0, 4));

      setFightNotificationHistory(prev => {
        const updated = [...newSystemNotifications, ...prev].slice(0, 50);
        localStorage.setItem("fightNotificationHistory", JSON.stringify(updated));
        return updated;
      });
    }

    if (isFirstRun) {
      hasInitializedNotifications.current = true;
    }
  }, [notificationsEnabled, fightNotificationsEnabled, notifiedChannels]);

  // Get API endpoint based on tracker type
  const getApiEndpoint = useCallback(() => {
    // In view-as mode, we don't fetch from API - we use cached data
    if (viewAsUser && trackerType === "personal") {
      return null; // Signal to use cached viewAsTrackers
    }
    if (trackerType === "personal" && user) {
      return `${API_URL}/api/personal/bosses`;
    } else if (trackerType === "guild" && selectedGuildId) {
      return `${API_URL}/api/guilds/${selectedGuildId}/bosses`;
    }
    return `${API_URL}/api/bosses`;
  }, [trackerType, selectedGuildId, user, viewAsUser]);

  // Fetch boss data
  const fetchBosses = useCallback(async () => {
    try {
      // In view-as mode for personal tracker, use cached data
      if (viewAsUser && trackerType === "personal" && viewAsTrackers.length > 0) {
        const processedBosses = processBackendData(viewAsTrackers);
        setBosses(processedBosses);
        previousBossesRef.current = processedBosses;
        setLoading(false);
        return;
      }

      const endpoint = getApiEndpoint();
      if (!endpoint) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(endpoint);
      const processedBosses = processBackendData(response.data);
      setBosses(processedBosses);
      previousBossesRef.current = processedBosses;
    } catch (error) {
      console.error("Failed to fetch bosses:", error);
      if (error.response?.status === 401 && trackerType !== "global") {
        setTrackerType("global");
        localStorage.setItem("trackerType", "global");
        toast.error("Session expired. Switched to global tracker.");
      }
    } finally {
      setLoading(false);
    }
  }, [getApiEndpoint, trackerType, viewAsUser, viewAsTrackers]);

  // Process backend data to group by boss name
  const processBackendData = (data) => {
    const groupedBosses = [];
    data.forEach((row) => {
      const parts = row.name.split(" - ");
      const baseName = parts[0];
      const locationName = parts.length > 1 ? parts.slice(1).join(" - ") : "Main Location";

      let group = groupedBosses.find((g) => g.name === baseName);
      if (!group) {
        group = {
          name: baseName,
          baseTime: row.base_time,
          imageKey: row.image_key,
          locations: [],
        };
        groupedBosses.push(group);
      }

      group.locations.push({
        locationName: locationName,
        backendName: row.name,
        channels: row.channels,
      });
    });
    return groupedBosses;
  };

  // Get boss image URL
  const getBossImage = (imageKey) => {
    return BOSS_IMAGE_MAPPING[imageKey] || "https://upload.wikimedia.org/wikipedia/commons/c/ca/1x1.png";
  };

  // Poll for updates
  useEffect(() => {
    setLoading(true);
    fetchBosses();

    const poll = () => {
      pollTimeoutRef.current = setTimeout(() => {
        fetchBosses();
        poll();
      }, POLL_INTERVAL);
    };
    poll();

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [fetchBosses]);

  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Check notifications when bosses or time changes
  useEffect(() => {
    if (notificationsEnabled && bosses.length > 0) {
      checkForNotifications(bosses, now);
    }
  }, [bosses, now, notificationsEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch channel history from backend for the currently selected boss
  const fetchChannelHistory = useCallback(async () => {
    if (!bosses.length) return;
    const selectedBoss = bosses[selectedBossIndex];
    if (!selectedBoss) return;
    const location = selectedBoss.locations[selectedLocationIndex];
    if (!location) return;

    try {
      const params = new URLSearchParams({
        boss_name: location.backendName,
        tracker_type: trackerType,
      });
      if (trackerType === "guild" && selectedGuildId) {
        params.append("guild_id", selectedGuildId);
      }
      const response = await axios.get(`${API_URL}/api/bosses/channel-history?${params}`);
      const historyMap = response.data.history || {};
      setChannelHistoryData(historyMap);

      // Also populate historyChannels for backward-compatible undo buttons
      const newHistoryChannels = {};
      for (const [chIdx, entries] of Object.entries(historyMap)) {
        if (entries.length > 0) {
          const latest = entries[0]; // most recent
          const key = `${location.backendName}-${chIdx}`;
          newHistoryChannels[key] = latest.previous_state;
        }
      }
      setHistoryChannels(prev => ({ ...prev, ...newHistoryChannels }));
    } catch {
      // Silently fail - history is non-critical
    }
  }, [bosses, selectedBossIndex, selectedLocationIndex, trackerType, selectedGuildId]);

  // Fetch channel history whenever boss, location or tracker changes
  useEffect(() => {
    fetchChannelHistory();
  }, [fetchChannelHistory]);

  // Poll for fight notifications
  useEffect(() => {
    if (!fightNotificationsEnabled) {
      setFightNotifications([]);
      return;
    }

    const fetchFightNotifications = async () => {
      try {
        const params = new URLSearchParams({ tracker_type: trackerType });
        if (selectedGuildId) params.append("guild_id", selectedGuildId);
        
        const response = await axios.get(`${API_URL}/api/fight/notifications?${params}`);
        
        // Filter out notifications we've already seen AND filter out old notifications
        // Only show notifications from the last 30 seconds to prevent spam on navigation
        const now = Date.now();
        const thirtySecondsAgo = now - 30000;
        
        const newNotifications = response.data.filter(n => {
          if (seenNotificationIds.current.has(n.id)) return false;
          // Check if notification is recent enough to display
          const notifTime = new Date(n.timestamp).getTime();
          return notifTime > thirtySecondsAgo;
        });
        
        if (newNotifications.length > 0) {
          // Mark as seen and add to state, include boss image
          newNotifications.forEach(n => {
            seenNotificationIds.current.add(n.id);
            // Try to find boss image
            const boss = bosses.find(b => 
              b.locations.some(loc => loc.backendName === n.boss_name || b.name === n.boss_name)
            );
            if (boss) {
              n.bossImage = getBossImage(boss.imageKey);
            }
          });
          
          // Add to active notifications (display)
          setFightNotifications(prev => [...newNotifications, ...prev].slice(0, 4));
          
          // Add to history (log)
          setFightNotificationHistory(prev => {
            const updated = [...newNotifications, ...prev].slice(0, 50);
            localStorage.setItem("fightNotificationHistory", JSON.stringify(updated));
            return updated;
          });
        }
        
        // Also add all notifications to seen set to prevent showing them later
        response.data.forEach(n => seenNotificationIds.current.add(n.id));
        
      } catch (error) {
        // Silently ignore notification fetch errors
      }
    };

    fetchFightNotifications();
    const interval = setInterval(fetchFightNotifications, 3000);
    return () => clearInterval(interval);
  }, [trackerType, selectedGuildId, fightNotificationsEnabled, bosses]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle fight notifications
  const toggleFightNotifications = useCallback(() => {
    setFightNotificationsEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem("fightNotificationsEnabled", newValue.toString());
      if (!newValue) {
        setFightNotifications([]); // Clear active notifications when disabled
      }
      return newValue;
    });
  }, []);

  // Toggle Hunter's Chance notifications
  const toggleHcNotifications = useCallback(() => {
    setHcNotificationsEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem("hcNotificationsEnabled", newValue.toString());
      return newValue;
    });
  }, []);

  // Dismiss fight notification
  const dismissFightNotification = useCallback((notificationId) => {
    setFightNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Delete fight log
  const handleDeleteLog = useCallback((notificationId) => {
    setFightNotificationHistory(prev => {
      const updated = prev.filter(n => n.id !== notificationId);
      localStorage.setItem("fightNotificationHistory", JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Clear all fight logs
  const handleClearAllLogs = useCallback(() => {
    setFightNotificationHistory([]);
    localStorage.removeItem("fightNotificationHistory");
  }, []);

  // Mark boss as killed
  const handleMarkKill = async (channelIndex) => {
    // Prevent editing in view-as mode
    if (viewAsUser) {
      toast.error("Cannot edit trackers while viewing as another user");
      return;
    }

    const selectedBoss = bosses[selectedBossIndex];
    const location = selectedBoss.locations[selectedLocationIndex];
    const key = `${location.backendName}-${channelIndex}`;

    setHistoryChannels((prev) => ({
      ...prev,
      [key]: { ...location.channels[channelIndex] },
    }));

    setSavingChannels((prev) => ({ ...prev, [key]: true }));

    try {
      // Determine sync guild id for personal tracker
      const syncGuildId = (trackerType === "personal" && syncToGuild && selectedSyncGuildId) 
        ? selectedSyncGuildId 
        : null;

      const response = await axios.post(`${API_URL}/api/bosses/kill`, {
        boss_name: location.backendName,
        channel_index: channelIndex,
        killer_name: userName || user?.username || "Anonymous",
        sync_to_global: syncToGlobal,
        tracker_type: trackerType,
        guild_id: selectedGuildId,
        sync_to_guild_id: syncGuildId,
        client_id: localStorage.getItem("guest_id"),
      });

      setBosses((prev) => {
        const updated = [...prev];
        updated[selectedBossIndex].locations[selectedLocationIndex].channels[channelIndex] = {
          time: response.data.time,
          killer: response.data.killer,
        };
        return updated;
      });

      // Clear notification flags for this channel
      setNotifiedChannels((prev) => {
        const updated = new Set(prev);
        updated.delete(`${key}-window`);
        updated.delete(`${key}-up`);
        return updated;
      });

      toast.success(`Marked ${selectedBoss.name} as killed on CH ${channelIndex + 1}`);
      
      // Check for spam warning
      if (response.data.spam_warning) {
        setTimeout(() => {
          toast.warning(response.data.warning_message, { duration: 8000 });
        }, 500);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to mark kill";
      toast.error(detail);
      setHistoryChannels((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    } finally {
      setSavingChannels((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Mark as killed by another player (anonymous, doesn't count in your stats)
  const handleKilledByOther = async (channelIndex) => {
    const selectedBoss = bosses[selectedBossIndex];
    const location = selectedBoss.locations[selectedLocationIndex];
    const key = `${location.backendName}-${channelIndex}`;

    // Save current state for undo
    const currentChannel = location.channels[channelIndex];
    setHistoryChannels((prev) => ({
      ...prev,
      [key]: currentChannel || { time: 0, killer: "" },
    }));

    setSavingChannels((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await axios.post(`${API_URL}/api/bosses/kill`, {
        boss_name: location.backendName,
        channel_index: channelIndex,
        killer_name: "Anonymous",
        sync_to_global: syncToGlobal,
        tracker_type: trackerType,
        guild_id: selectedGuildId,
        killed_by_other: true, // Flag to not count in user stats
        client_id: localStorage.getItem("guest_id"),
      });

      setBosses((prev) => {
        const updated = [...prev];
        updated[selectedBossIndex].locations[selectedLocationIndex].channels[channelIndex] = {
          time: response.data.time,
          killer: response.data.killer,
        };
        return updated;
      });

      toast.success(`Marked as killed by other player on CH ${channelIndex + 1}`);
      
      // Check for spam warning
      if (response.data.spam_warning) {
        setTimeout(() => {
          toast.warning(response.data.warning_message, { duration: 8000 });
        }, 500);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to mark kill";
      toast.error(detail);
      setHistoryChannels((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    } finally {
      setSavingChannels((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Start fight - notify others before killing
  const handleStartFight = async (channelIndex) => {
    if (!user) {
      toast.error("You must be logged in to start a fight");
      return;
    }

    // Check if fight alerts are enabled
    if (!fightNotificationsEnabled) {
      toast.error("Fight alerts are disabled. Enable them to notify others.");
      return;
    }

    const selectedBoss = bosses[selectedBossIndex];
    const location = selectedBoss.locations[selectedLocationIndex];

    try {
      await axios.post(`${API_URL}/api/fight/start`, {
        boss_name: location.backendName,
        channel_index: channelIndex,
        location_name: location.locationName,
        notification_type: "start_fight",
        tracker_type: trackerType,
        guild_id: selectedGuildId,
      });

      toast.success(`Fight started! Others will be notified.`);
    } catch (error) {
      toast.error("Failed to announce fight");
    }
  };

  // Clear kill
  const handleClearKill = async (channelIndex) => {
    // Prevent editing in view-as mode
    if (viewAsUser) {
      toast.error("Cannot edit trackers while viewing as another user");
      return;
    }

    const selectedBoss = bosses[selectedBossIndex];
    const location = selectedBoss.locations[selectedLocationIndex];
    const key = `${location.backendName}-${channelIndex}`;

    setHistoryChannels((prev) => ({
      ...prev,
      [key]: { ...location.channels[channelIndex] },
    }));

    setSavingChannels((prev) => ({ ...prev, [key]: true }));

    try {
      await axios.post(`${API_URL}/api/bosses/clear`, {
        boss_name: location.backendName,
        channel_index: channelIndex,
        sync_to_global: syncToGlobal,
        tracker_type: trackerType,
        guild_id: selectedGuildId,
      });

      setBosses((prev) => {
        const updated = [...prev];
        updated[selectedBossIndex].locations[selectedLocationIndex].channels[channelIndex] = {
          time: 0,
          killer: "",
        };
        return updated;
      });

      toast.success(`Cleared timer for ${selectedBoss.name} on CH ${channelIndex + 1}`);
    } catch (error) {
      toast.error("Failed to clear timer");
      setHistoryChannels((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    } finally {
      setSavingChannels((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Undo kill - uses backend-driven revert
  const handleUndoKill = async (channelIndex) => {
    // Prevent editing in view-as mode
    if (viewAsUser) {
      toast.error("Cannot edit trackers while viewing as another user");
      return;
    }

    const selectedBoss = bosses[selectedBossIndex];
    const location = selectedBoss.locations[selectedLocationIndex];
    const key = `${location.backendName}-${channelIndex}`;

    // Check if there's backend history for this channel
    const chHistory = channelHistoryData[String(channelIndex)];
    if (chHistory && chHistory.length > 0) {
      const latestEntry = chHistory[0];
      setSavingChannels((prev) => ({ ...prev, [key]: true }));
      try {
        const response = await axios.post(`${API_URL}/api/bosses/channel-revert/${latestEntry.id}`);
        const restored = response.data.restored_state;
        setBosses((prev) => {
          const updated = [...prev];
          updated[selectedBossIndex].locations[selectedLocationIndex].channels[channelIndex] = {
            time: restored.time,
            killer: restored.killer,
          };
          return updated;
        });
        // Refresh history after revert
        fetchChannelHistory();
        toast.success("Undo successful");
      } catch (error) {
        const msg = error.response?.data?.detail || "Failed to undo";
        toast.error(msg);
      } finally {
        setSavingChannels((prev) => ({ ...prev, [key]: false }));
      }
      return;
    }

    // Fallback to local history (in case backend history is not available)
    const previousState = historyChannels[key];
    if (!previousState) return;

    setSavingChannels((prev) => ({ ...prev, [key]: true }));

    try {
      await axios.post(`${API_URL}/api/bosses/restore`, {
        boss_name: location.backendName,
        channel_index: channelIndex,
        timestamp: previousState.time,
        killer_name: previousState.killer,
        tracker_type: trackerType,
        guild_id: selectedGuildId,
      });

      setBosses((prev) => {
        const updated = [...prev];
        updated[selectedBossIndex].locations[selectedLocationIndex].channels[channelIndex] = {
          ...previousState,
        };
        return updated;
      });

      setHistoryChannels((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });

      toast.success("Undo successful");
    } catch (error) {
      toast.error("Failed to undo");
    } finally {
      setSavingChannels((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Admin revert - revert a specific history entry by ID
  const handleAdminRevert = async (historyId, channelIndex) => {
    const selectedBoss = bosses[selectedBossIndex];
    const location = selectedBoss.locations[selectedLocationIndex];
    const key = `${location.backendName}-${channelIndex}`;

    setSavingChannels((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await axios.post(`${API_URL}/api/bosses/channel-revert/${historyId}`);
      const restored = response.data.restored_state;
      setBosses((prev) => {
        const updated = [...prev];
        updated[selectedBossIndex].locations[selectedLocationIndex].channels[channelIndex] = {
          time: restored.time,
          killer: restored.killer,
        };
        return updated;
      });
      fetchChannelHistory();
      toast.success("Reverted successfully");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to revert";
      toast.error(msg);
    } finally {
      setSavingChannels((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Select boss
  const handleSelectBoss = (index) => {
    setSelectedBossIndex(index);
    localStorage.setItem("selectedBossIndex", index);
    setSelectedLocationIndex(0);
    localStorage.setItem("selectedLocationIndex", 0);
    setSidebarOpen(false);
  };

  // Handle location index change wrapper
  const handleLocationIndexChange = (index) => {
    setSelectedLocationIndex(index);
    localStorage.setItem("selectedLocationIndex", index);
  };

  // Handle tracker type change
  const handleTrackerTypeChange = (type, guildId = null) => {
    if ((type === "personal" || type === "guild") && !user) {
      setShowAuthPrompt(true);
      return;
    }
    setTrackerType(type);
    localStorage.setItem("trackerType", type);
    setSelectedGuildId(guildId);
    if (guildId) {
      localStorage.setItem("selectedGuildId", guildId);
    } else {
      localStorage.removeItem("selectedGuildId");
    }
    // Don't reset boss selection - keep the currently selected boss
    // setSelectedBossIndex(0);
    // setSelectedLocationIndex(0);
  };

  // Sync revert state
  const [lastSyncId, setLastSyncId] = useState(null);
  const [canRevertUntil, setCanRevertUntil] = useState(null);

  // Sync from global
  const handleSyncFromGlobal = async () => {
    if (!user) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/bosses/sync-from-global`, {
        tracker_type: trackerType,
        guild_id: selectedGuildId,
      });
      
      // Store sync ID for potential revert
      setLastSyncId(response.data.sync_id);
      setCanRevertUntil(new Date(response.data.can_revert_until));
      
      // Clear revert option after 3 minutes
      setTimeout(() => {
        setLastSyncId(null);
        setCanRevertUntil(null);
      }, 3 * 60 * 1000);
      
      toast.success("Synced from global tracker! You can revert within 3 minutes.", {
        duration: 5000,
      });
      fetchBosses();
    } catch (error) {
      toast.error("Failed to sync from global");
    }
  };

  // Revert sync
  const handleRevertSync = async () => {
    if (!lastSyncId) return;
    
    try {
      await axios.post(`${API_URL}/api/bosses/revert-sync/${lastSyncId}`);
      setLastSyncId(null);
      setCanRevertUntil(null);
      toast.success("Sync reverted successfully!");
      fetchBosses();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to revert sync";
      toast.error(message);
    }
  };

  const selectedBoss = bosses[selectedBossIndex];
  const selectedGuild = guilds.find((g) => g.id === selectedGuildId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading boss data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden" data-testid="dashboard">
      {/* Fight Notifications */}
      {fightNotificationsEnabled && (
        <FightNotificationContainer 
          notifications={fightNotifications} 
          onDismiss={dismissFightNotification}
          getBossImage={getBossImage}
        />
      )}

      {/* View as User Banner - Only shown when admin is viewing another user's tracker */}
      {viewAsUser && (
        <div className="bg-purple-600/20 border-b border-purple-500/30 px-4 py-2">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UserCog className="w-5 h-5 text-purple-400" />
              <span className="text-purple-200 font-medium">
                Viewing as: <span className="text-purple-100 font-bold">{viewAsUser.username}</span>
              </span>
              <span className="text-purple-300/60 text-sm">
                ({viewAsUser.email})
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={exitViewAsMode}
              className="border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
            >
              <X className="w-4 h-4 mr-1" />
              Exit View
            </Button>
          </div>
        </div>
      )}

      {/* Quick Access: Active Expeditions */}
      <ExpeditionQuickBar />

      {/* Tracker Tabs Header */}
      <TrackerTabs
        trackerType={trackerType}
        selectedGuildId={selectedGuildId}
        guilds={guilds}
        user={user}
        onTrackerTypeChange={handleTrackerTypeChange}
        onManageGuilds={() => setShowGuildManager(true)}
        onLogin={() => setShowAuthPrompt(true)}
        compactMode={compactMode}
        onToggleCompact={handleToggleCompact}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden lg:flex lg:flex-col w-80 flex-shrink-0 border-r border-border h-full overflow-hidden">
          <BossSidebar
            bosses={bosses}
            selectedBossIndex={selectedBossIndex}
            onSelectBoss={handleSelectBoss}
            getBossImage={getBossImage}
            now={now}
            userName={userName}
            onUserNameChange={handleUserNameChange}
            user={user}
            trackerType={trackerType}
            syncToGlobal={syncToGlobal}
            onSyncToGlobalChange={handleSyncToGlobalChange}
            onSyncFromGlobal={handleSyncFromGlobal}
            guilds={guilds}
            syncToGuild={syncToGuild}
            onSyncToGuildChange={handleSyncToGuildChange}
            selectedSyncGuildId={selectedSyncGuildId}
            onSelectedSyncGuildChange={handleSelectedSyncGuildChange}
            favorites={favorites}
            onToggleFavorite={handleToggleFavorite}
            showFavoritesOnly={showFavoritesOnly}
            onShowFavoritesOnlyChange={handleShowFavoritesOnlyChange}
          />
        </div>

        {/* Mobile Sidebar */}
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild className="lg:hidden fixed top-16 left-4 z-50">
            <Button variant="outline" size="icon" data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 p-0">
            <BossSidebar
              bosses={bosses}
              selectedBossIndex={selectedBossIndex}
              onSelectBoss={handleSelectBoss}
              getBossImage={getBossImage}
              now={now}
              userName={userName}
              onUserNameChange={handleUserNameChange}
              user={user}
              trackerType={trackerType}
              syncToGlobal={syncToGlobal}
              onSyncToGlobalChange={handleSyncToGlobalChange}
              onSyncFromGlobal={handleSyncFromGlobal}
              lastSyncId={lastSyncId}
              canRevertUntil={canRevertUntil}
              onRevertSync={handleRevertSync}
              guilds={guilds}
              syncToGuild={syncToGuild}
              onSyncToGuildChange={handleSyncToGuildChange}
              selectedSyncGuildId={selectedSyncGuildId}
              onSelectedSyncGuildChange={handleSelectedSyncGuildChange}
              favorites={favorites}
              onToggleFavorite={handleToggleFavorite}
              showFavoritesOnly={showFavoritesOnly}
              onShowFavoritesOnlyChange={handleShowFavoritesOnlyChange}
            />
          </SheetContent>
        </Sheet>

        {/* Main Content */}
        <main className="flex-1 overflow-auto h-full">
          {/* Mobile Header */}
          <div className="lg:hidden sticky top-0 z-40 glass p-4 flex justify-between items-center">
            <div className="pl-12">
              <h1 className="font-bold text-lg">{selectedBoss?.name || "Select a Boss"}</h1>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Your name"
                value={userName}
                onChange={handleUserNameChange}
                className="w-28 h-8 text-sm"
                data-testid="mobile-username-input"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleNotifications}
                data-testid="notifications-button-mobile"
              >
                {notificationsEnabled ? <Bell className="h-4 w-4 text-primary" /> : <BellOff className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={fetchBosses}
                data-testid="refresh-button-mobile"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {selectedBoss && (
            <ChannelGrid
              boss={selectedBoss}
              selectedLocationIndex={selectedLocationIndex}
              setSelectedLocationIndex={handleLocationIndexChange}
              now={now}
              getBossImage={getBossImage}
              onMarkKill={handleMarkKill}
              onStartFight={handleStartFight}
              onClearKill={handleClearKill}
              onUndoKill={handleUndoKill}
              onKilledByOther={handleKilledByOther}
              savingChannels={savingChannels}
              historyChannels={historyChannels}
              notificationsEnabled={notificationsEnabled}
              toggleNotifications={toggleNotifications}
              fightNotificationsEnabled={fightNotificationsEnabled}
              toggleFightNotifications={toggleFightNotifications}
              hcNotificationsEnabled={hcNotificationsEnabled}
              toggleHcNotifications={toggleHcNotifications}
              onShowFightLog={() => setShowFightLog(true)}
              fightLogCount={fightNotificationHistory.length}
              trackerType={trackerType}
              selectedGuild={selectedGuild}
              user={user}
              syncToGlobal={syncToGlobal}
              channelHistoryData={channelHistoryData}
              onAdminRevert={handleAdminRevert}
              compactMode={compactMode}
            />
          )}
        </main>
      </div>

      {/* Guild Manager Modal */}
      <GuildManager
        open={showGuildManager}
        onOpenChange={setShowGuildManager}
        onSelectGuild={(guildId) => {
          handleTrackerTypeChange("guild", guildId);
          setShowGuildManager(false);
        }}
      />

      {/* Auth Prompt */}
      <AuthPrompt
        open={showAuthPrompt}
        onOpenChange={setShowAuthPrompt}
      />

      {/* Username Setup Modal for OAuth users */}
      <UsernameSetupModal />

      {/* Fight Notification Log Modal */}
      <FightNotificationLog
        open={showFightLog}
        onOpenChange={setShowFightLog}
        notifications={fightNotificationHistory}
        onClearAll={handleClearAllLogs}
        onDelete={handleDeleteLog}
      />
    </div>
  );
};

export default Dashboard;
